import { type LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface EmptyStateAction {
  label: string;
  onClick?: () => void;
  href?: string;
  variant?: "hero" | "outline" | "ghost";
}

interface EmptyStateProps {
  icon: LucideIcon;
  title: string;
  description?: string;
  primaryAction?: EmptyStateAction;
  secondaryAction?: EmptyStateAction;
  className?: string;
}

/**
 * Composant d'état vide réutilisable.
 * Affiche une icône, un titre, une description et jusqu'à 2 boutons d'action.
 */
const EmptyState = ({
  icon: Icon,
  title,
  description,
  primaryAction,
  secondaryAction,
  className,
}: EmptyStateProps) => {
  return (
    <div className={cn("py-16 text-center flex flex-col items-center", className)}>
      <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-muted">
        <Icon className="h-7 w-7 text-muted-foreground/60" aria-hidden="true" />
      </div>

      <h3 className="font-display text-base font-semibold text-foreground leading-snug max-w-xs">
        {title}
      </h3>

      {description && (
        <p className="mt-2 text-sm text-muted-foreground max-w-sm leading-relaxed">
          {description}
        </p>
      )}

      {(primaryAction || secondaryAction) && (
        <div className="mt-6 flex flex-col sm:flex-row gap-3 items-center">
          {primaryAction && (
            primaryAction.href ? (
              <a href={primaryAction.href}>
                <Button variant={primaryAction.variant ?? "hero"}>
                  {primaryAction.label}
                </Button>
              </a>
            ) : (
              <Button variant={primaryAction.variant ?? "hero"} onClick={primaryAction.onClick}>
                {primaryAction.label}
              </Button>
            )
          )}
          {secondaryAction && (
            secondaryAction.href ? (
              <a href={secondaryAction.href}>
                <Button variant={secondaryAction.variant ?? "outline"}>
                  {secondaryAction.label}
                </Button>
              </a>
            ) : (
              <Button variant={secondaryAction.variant ?? "outline"} onClick={secondaryAction.onClick}>
                {secondaryAction.label}
              </Button>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default EmptyState;
